import type { TimeWindow } from '@ops/features/filter-cards';

// URL-truth for the Operations Dashboard v2 page (shareable / refresh-safe).
// Distinct from the /operations list `OperationsSearch` — this page owns its own
// window + selected-card + scope state (XT-225 six cards).
export type OpsDashboardSearch = {
  /** Time window; drives every card count and the list. Default 'today'. */
  window?: TimeWindow;
  /**
   * Custom range endpoints (ISO). Only used when `window === 'custom'`.
   */
  startDate?: string;
  endDate?: string;
  /** Selected filter-card id (overlays its preset onto the list). */
  card?: string;
  search?: string;
  searchField?: 'optigest' | 'traveler' | 'customer' | 'supplier';
  /** Scope toggle. 'mine' narrows to the caller's items. */
  scope?: 'all' | 'mine';
  sortBy?: 'product' | 'date' | 'status';
  sortOrder?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
};

const TIME_WINDOW_VALUES: TimeWindow[] = ['today', 'next3', 'week', 'custom'];
const SEARCH_FIELDS = ['optigest', 'traveler', 'customer', 'supplier'] as const;
const SORT_FIELDS = ['product', 'date', 'status'] as const;
export const OPS_DASHBOARD_PAGE_SIZES = [20, 40, 60, 100] as const;

const asEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

const asIsoDate = (value: unknown): string | undefined =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
    ? value
    : undefined;

// TanStack Router `validateSearch` — coerce/whitelist each field so a hand-typed
// or stale URL never crashes the page (mirrors validateOperationsSearch).
export const validateOpsDashboardSearch = (
  input: Record<string, unknown>
): OpsDashboardSearch => ({
  window: asEnum(input.window, TIME_WINDOW_VALUES),
  startDate: asIsoDate(input.startDate),
  endDate: asIsoDate(input.endDate),
  card: typeof input.card === 'string' ? input.card : undefined,
  search: typeof input.search === 'string' ? input.search : undefined,
  searchField: asEnum(input.searchField, SEARCH_FIELDS),
  scope: asEnum(input.scope, ['all', 'mine'] as const),
  sortBy: asEnum(input.sortBy, SORT_FIELDS),
  sortOrder: asEnum(input.sortOrder, ['asc', 'desc'] as const),
  offset: typeof input.offset === 'number' ? input.offset : undefined,
  limit:
    typeof input.limit === 'number' &&
    (OPS_DASHBOARD_PAGE_SIZES as readonly number[]).includes(input.limit)
      ? input.limit
      : undefined,
});
